/**
 * /upload/:token — entry point for upload-link recipients.
 *
 * Exchanges the public upload-link token for a scoped guest JWT, stores it in
 * place of the regular auth token, and redirects to the same authenticated
 * order detail page (in "guest mode"). The recipient sees the exact Master
 * Files UX as the customer — same component, same backend — but the rest of
 * the app is hidden by guards in OrderDetailPage.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ShieldAlert, Lock } from 'lucide-react';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

export default function UploadInviteRedeemPage() {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const [phase, setPhase] = useState('loading'); // loading | password | exchanging | error
    const [info, setInfo] = useState(null);
    const [password, setPassword] = useState('');
    const [accessToken, setAccessToken] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    // Step 1: probe the link
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/upload-links/${token}`);
                if (cancelled) return;
                setInfo(res.data);
                if (res.data.has_password) {
                    setPhase('password');
                } else {
                    await exchangeForGuestToken();
                }
            } catch (err) {
                if (cancelled) return;
                const detail = err.response?.data?.detail;
                if (err.response?.status === 410) setError(detail || t('upload_invite.expired', 'This upload link has expired or been revoked.'));
                else if (err.response?.status === 404) setError(t('upload_invite.not_found', 'Upload link not found.'));
                else setError(detail || t('upload_invite.load_failed', 'Could not load upload link.'));
                setPhase('error');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const verifyPassword = async () => {
        setBusy(true);
        try {
            const res = await axios.post(`${API}/upload-links/${token}/verify-password`, { password });
            setAccessToken(res.data.access_token);
            await exchangeForGuestToken(res.data.access_token);
        } catch (err) {
            setError(err.response?.data?.detail || t('upload_invite.incorrect_password', 'Incorrect password'));
        } finally {
            setBusy(false);
        }
    };

    const exchangeForGuestToken = async (sessionToken) => {
        setPhase('exchanging');
        try {
            const res = await axios.post(`${API}/upload-links/${token}/redeem`, {
                access_token: sessionToken || accessToken || undefined,
            });
            const { guest_token, order_id, expires_at } = res.data;
            localStorage.setItem('token', guest_token);
            localStorage.setItem('guest_session', JSON.stringify({
                upload_link_token: token,
                order_id,
                expires_at,
                redeemed_at: new Date().toISOString(),
            }));
            window.location.replace(`/orders/${order_id}/files?guest=1`);
        } catch (err) {
            setError(err.response?.data?.detail || t('upload_invite.session_failed', 'Could not start your upload session.'));
            setPhase('error');
        }
    };

    if (phase === 'loading' || phase === 'exchanging') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center space-y-3">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
                    <p className="text-sm text-slate-500">
                        {phase === 'exchanging'
                            ? t('upload_invite.opening_workspace', 'Opening your upload workspace…')
                            : t('common.loading', 'Loading…')}
                    </p>
                </div>
            </div>
        );
    }

    if (phase === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
                <Card className="max-w-md w-full">
                    <CardContent className="py-10 text-center space-y-3">
                        <ShieldAlert className="h-10 w-10 mx-auto text-amber-500" />
                        <h1 className="text-lg font-semibold text-slate-900">{t('upload_invite.cannot_open', "Can't open this link")}</h1>
                        <p className="text-sm text-slate-500">{error}</p>
                        <Button variant="outline" onClick={() => navigate('/login')}>{t('common.go_to_signin', 'Go to sign in')}</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <Card className="max-w-md w-full" data-testid="upload-password-card">
                <CardContent className="py-8 space-y-4">
                    <div className="text-center">
                        <Lock className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                        <h1 className="text-lg font-semibold text-slate-900">{t('upload_invite.password_required', 'Password required')}</h1>
                        <p className="text-sm text-slate-500">
                            {info?.order_number && t('upload_invite.order_prefix', 'Order #{n} · ', { n: info.order_number })}
                            {t('upload_invite.password_instruction', 'Enter the password your contact shared with you.')}
                        </p>
                    </div>
                    <div>
                        <Label htmlFor="pwd">{t('common.password', 'Password')}</Label>
                        <Input
                            id="pwd"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && verifyPassword()}
                            data-testid="upload-password-input"
                        />
                        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
                    </div>
                    <Button
                        onClick={verifyPassword}
                        disabled={busy || !password}
                        className="w-full text-white border-0 shadow-md"
                        style={{ backgroundImage: 'linear-gradient(135deg, hsl(35 92% 45%), hsl(42 96% 55%))' }}
                        data-testid="upload-password-submit"
                    >
                        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        {t('common.continue', 'Continue')}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
